import { LinkService } from './../../../services/general/link.service';
import { ConfigService } from 'src/app/services/general/config.service';
import { AccountsListService } from 'src/app/services/accounts/accounts-list.service';
import { NavigationExtras, Router } from '@angular/router';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController, ActionSheetController, Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';

import { OnlineService } from 'src/app/services/general/online.service';
import { AccountService } from 'src/app/services/account/account.service';
import { CreditService } from 'src/app/services/subscription/credit.service';
import { PopupService } from 'src/app/services/general/popup.service';

import { AccountInfoComponent } from 'src/app/components/account/account-info/account-info.component';

import { Credit } from 'src/app/interfaces/credit';
import { AccountInfo, UserAccounts } from 'src/app/interfaces/account';
import { AccountsSetupComponent } from 'src/app/components/accounts/accounts-setup/accounts-setup.component';
import { ErrorService } from 'src/app/services/general/error.service';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DateTimeService } from 'src/app/services/general/date-time.service';

@Component({
  selector: 'app-user-credit',
  templateUrl: './user-credit.page.html',
  styleUrls: ['./user-credit.page.scss'],
})
export class UserCreditPage implements OnInit, OnDestroy {

  @ViewChild(CdkVirtualScrollViewport) cdkVirtualScrollViewport: CdkVirtualScrollViewport;
  /**
   * type
   */
  type: string;
  /**
   * Credit list
   */
  creditList: Credit[];
  /**
   * Account ID
   */
  accountId: string;

  /**
   * User credit subscription
   */
  private userCreditSubscription: Subscription;

  /**
   * Constructor
   * @param router router
   * @param modalController  modal controller
   * @param fns firebase functions service
   * @param translate translate
   * @param accountService account service
   * @param creditService credit service
   * @param onlineService online service
   * @param popupService popup service
   */
  constructor(
    private platform: Platform,
    private router: Router,
    private actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private fns: AngularFireFunctions,
    private translate: TranslateService,
    private accountsListService: AccountsListService,
    private accountService: AccountService,
    private dateTimeService: DateTimeService,
    private creditService: CreditService,
    private onlineService: OnlineService,
    private popupService: PopupService,
    private errorService: ErrorService,
  ) { }

  ngOnInit(): void {
      
  }

  ngOnDestroy(): void {
    this.unwatchUserCredit();
  }

  /**
   * before view enter
   */
  async ionViewWillEnter() {
    if (!this.type) {
      this.type = 'active';
    }
    this.accountId = await this.accountService.readAccountId();
    this.watchUserCredit();
  }

  /**
   * Before view leave
   */
  ionViewWillLeave() {
    this.unwatchUserCredit();
  }

  /**
   * Watch user credit
   */
  async watchUserCredit() {
    if (!this.userCreditSubscription) {
      this.userCreditSubscription = this.creditService.observableCreditList.subscribe(() => {
        this.searchCredit();
      });
    }
    
  }

  /**
   * Unwatch user credit
   */
  async unwatchUserCredit() {
    if (this.userCreditSubscription) {
      this.userCreditSubscription.unsubscribe();
      this.userCreditSubscription = null;
    }
  }

  setupViewport() {
    if (this.cdkVirtualScrollViewport) {
      setTimeout(() => {
        this.cdkVirtualScrollViewport?.checkViewportSize();
      }, 200);
    } else {
      setTimeout(() => {
        this.setupViewport();
      }, 200);
    }
  }

  /**
   * Search credit
   */
  searchCredit() {
    this.creditList = this.creditService.searchCredit(this.type);
    this.setupViewport();
  }

  /**
   * Get account info by credit id cloud function call
   * @param creditId credit id
   * @returns account info
   */
  async getAccountInfoByCreditIdCall(creditId: string): Promise<AccountInfo> {
    let accountInfo: AccountInfo = {} as AccountInfo;
    if (creditId && this.onlineService.isOnline()) {
      await this.popupService.presentLoading();
      accountInfo = await this.fns.httpsCallable('getAccountInfoByCreditIdCall')({
        creditId
      }).toPromise().then(async (data: AccountInfo) => {
        return data;
      }).catch((err: any) => {
        this.errorService.logError(err);
        return null;
      });
    }
    return accountInfo;
  }

  format(timestamp: number, dateTimeFormat?: string, timezone?: string) {
    return this.dateTimeService.format(timestamp, dateTimeFormat, '', timezone);
  }

  /**
   * Action
   * @param credit credit
   */
  async action(credit: Credit) {
    if (this.onlineService.isOnline()) {
      if (credit?.creditId) {
        if (credit.redeemed) {
          const accountInfo: AccountInfo = await this.getAccountInfoByCreditIdCall(credit.creditId);
          if (accountInfo) {
            this.presentAccountInfoModal(accountInfo);
          } else {
            this.popupService.presentActionError();
          }
        } else if (!credit?.upgrade) {
          // if (await this.accountService.readAccountId()) {
          //   await this.accountService.logoutAccount();
          // }
          this.presentRedeemCreditActionSheet(credit.creditId);
        }
      } else {
        this.popupService.presentActionError();
      }
    }
  }

  /**
   * Present account info modal
   * @param accountInfo account info
   */
  async presentAccountInfoModal(accountInfo: AccountInfo) {
    if (accountInfo) {
      const modal = await this.modalController.create({
        component: AccountInfoComponent,
        cssClass: 'modal-transparent',
        componentProps: {
          accountInfo
        }
      });
      await this.popupService.dismissLoading();
      modal.present();
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Present account setup modal
   */
  async presentAccountSetupModal(creditId?: string) {
    // const newAccountMode = await this.accountService.readAccountId() ? true : false;
    // if (newAccountMode) {
    //   await this.accountService.logoutAccount();
    // }
    const modal = await this.modalController.create({
      component: AccountsSetupComponent,
      componentProps: {
        premium: true,
        creditId,
        // newAccountMode,
      }
    });
    modal.present();
  }

  /**
   * Prompt upgrade trial account
   * @param creditId Credit ID
   * @param userAccounts User account
   */
  async promptRedeemTrialAccount(userAccounts: UserAccounts, creditId: string) {
    if (creditId && userAccounts?.accountId) {
      const modal = await this.popupService.presentConfirm(
        this.translate.instant('ACCOUNT.msg.confirm_redeem_trial', { title: userAccounts?.title?.value })
      );
      modal.onWillDismiss().then((result: any) => {
        if (result?.data?.confirm) {
          const credit = this.creditService.getCreditById(creditId);
          if (credit?.creditId && credit?.enable && !credit?.redeemed) {
            this.accountService.upgradeAccountCall(credit, userAccounts.accountId, true);
          } else {
            this.popupService.presentActionError();
          }
        }
      });
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Present redeem credit action sheet
   * @param creditId Credit ID
   */
  async presentRedeemCreditActionSheet(creditId: string) {
    if (creditId) {
      const buttons: any[] = [];
      const userTrialAccount: UserAccounts[] = this.accountsListService.getOwnTrialAccount();
      if (userTrialAccount?.length) {
        buttons.push({
          text: this.translate.instant('PRODUCT.title.upgrade_trial'),
          handler: () => {
            this.promptRedeemTrialAccount(userTrialAccount[0], creditId);
          }
        });
      }
      buttons.push({
        text: this.translate.instant('PAYMENT.btn.setup'),
        handler: () => {
          this.presentAccountSetupModal(creditId);
        }
      }, {
        text: this.translate.instant('PAYMENT.btn.gift'),
        handler: () => {
          this.creditService.sendAsGift(creditId);
        }
      }, {
        text: this.translate.instant('BTN.cancel'),
        icon: 'close',
        role: 'cancel',
        handler: () => {
        }
      });
      const actionSheet = await this.actionSheetController.create({
        header: this.translate.instant('CREDIT.lbl.redeem_credit'),
        buttons
      });
      actionSheet.present();
    } else {
      this.popupService.presentActionError();
    }
  }

  /**
   * Go user profile page
   */
  goUserProfilePage() {
    this.router.navigate(['/user/profile']);
  }

  /**
   * Go subscription account page
   */
  goSubscriptionAccountPage() {
    const navigationExtras: NavigationExtras = { state: {} };
    if (this.platform.is('ios') && this.platform.is('hybrid')) {
      navigationExtras.state.backPremiumPage = true;
    }
    this.router.navigate([ '/subscription/premium' ], navigationExtras);
  }

  /**
   * Calculate item height for virtual scroll
   * @param item Item
   */
  itemHeightFn(credit: Credit) {
    let height = 116.06;
    if (credit?.gift) {
      height += 20.53;
    }
    return height;
  }

  /**
   * Track item
   * @param index Index
   * @param item item
   */
  trackByFn(index: number, item: Credit) {
    if (item?.creditId) {
      return item.creditId;
    }
    return index;
  }
}
